import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, CalendarDays, ChevronRight, MapPin, Store } from 'lucide-react';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { RichTextRenderer, Seo } from '@/components/common';
import { useCoverImageUrl } from '@/hooks/useMediaAssets';
import { useFleaMarketOccurrenceGalleryUrlMap } from '@/hooks/useFleaMarketOccurrenceMedia';
import { usePublicFleaMarketDetail, usePublicFleaMarketOccurrences } from '@/hooks/usePublicFleaMarkets';
import {
  formatOccurrenceDateShortSv,
  formatOccurrenceRangeSv,
  formatOccurrenceTimeRangeSv,
  getNextUpcomingOccurrence,
  normalizeOccurrenceDescriptionOverride,
  resolveOccurrenceImages,
} from '@/lib/fleaMarkets/occurrences';

export default function FleaMarketDetailPage() {
  const { fleaMarketId } = useParams<{ fleaMarketId: string }>();

  const { data: detail, isLoading: detailLoading } = usePublicFleaMarketDetail(fleaMarketId);
  const { data: occurrences, isLoading: occLoading } = usePublicFleaMarketOccurrences(fleaMarketId);
  const occurrenceIds = (occurrences ?? []).map((occ) => occ.id);
  const { data: occurrenceImageMap = {} } = useFleaMarketOccurrenceGalleryUrlMap(occurrenceIds);

  const coverUrl = useCoverImageUrl('flea_market', fleaMarketId ?? null, 640);
  const nextOccurrence = getNextUpcomingOccurrence(occurrences ?? []);

  const isLoading = detailLoading || occLoading;

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <SiteHeader density="compact" />
        <main className="flex-1 container py-8">
          <Skeleton className="h-5 w-24 mb-6" />
          <Skeleton className="h-10 w-1/2 mb-4" />
          <Skeleton className="h-64 w-full max-w-3xl rounded-xl mb-6" />
          <Skeleton className="h-5 w-2/3 mb-2" />
          <Skeleton className="h-5 w-1/2" />
        </main>
        <SiteFooter />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Seo title="Loppisen hittades inte" noIndex />
        <SiteHeader density="compact" />
        <main className="flex-1 container py-16 text-center">
          <h1 className="font-display text-2xl md:text-3xl mb-4 text-foreground">Loppisen hittades inte</h1>
          <p className="text-muted-foreground mb-8">Den loppis du letade efter är inte tillgänglig just nu.</p>
          <Button asChild>
            <Link to="/explore">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Tillbaka till Utforska
            </Link>
          </Button>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const canonicalUrl = `${window.location.origin}/loppis/${detail.flea_market_id}`;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Seo
        title={`${detail.title} – Loppis`}
        description={detail.description ?? `Loppis hos ${detail.producer_name}`}
        canonicalUrl={canonicalUrl}
        ogType="article"
        image={coverUrl ?? undefined}
        imageAlt={detail.title}
      />

      <SiteHeader density="compact" />

      <main className="flex-1 container py-6 md:py-10">
        <div className="max-w-3xl">
          <Button variant="ghost" size="sm" className="mb-4" asChild>
            <Link to={detail.place_slug ? `/place/${detail.place_slug}` : '/explore'}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Tillbaka
            </Link>
          </Button>

          <div className="space-y-4">
            <h1 className="font-display text-3xl text-foreground">{detail.title}</h1>

            <p className="text-sm text-muted-foreground -mt-1">
              {nextOccurrence
                ? `Återkommande loppis \u00b7 Nästa tillfälle ${formatOccurrenceDateShortSv(nextOccurrence.start_at)}`
                : `Återkommande loppis i ${detail.place_name}`}
            </p>

            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <Store className="h-4 w-4" />
                {detail.producer_slug ? <Link to={`/producer/${detail.producer_slug}`} className="hover:underline">{detail.producer_name}</Link> : detail.producer_name}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-4 w-4" />
                {detail.place_slug ? <Link to={`/place/${detail.place_slug}`} className="hover:underline">{detail.place_name}</Link> : detail.place_name}
              </span>
            </div>

            <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
              <div className="aspect-[16/7] bg-muted">
                {coverUrl ? (
                  <img src={coverUrl} alt={detail.title} className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-sm text-muted-foreground">Ingen omslagsbild</div>
                )}
              </div>
            </div>

            <section className="rounded-xl border border-border/60 bg-card p-5">
              <h2 className="font-medium text-foreground mb-2">Beskrivning</h2>
              {detail.description ? (
                <div className="prose prose-neutral max-w-none prose-p:my-3 prose-ul:my-3 prose-li:my-1 prose-hr:my-6">
                  <RichTextRenderer html={detail.description} />
                </div>
              ) : (
                <p className="text-sm text-foreground/90 leading-relaxed">Loppis med återbruk och lokala fynd.</p>
              )}
            </section>

            <section className="rounded-xl border border-border/60 bg-card p-5">
              <div className="flex items-center gap-2 mb-3">
                <CalendarDays className="h-4 w-4 text-primary" />
                <h2 className="font-medium text-foreground">Nästa loppis</h2>
              </div>
              {nextOccurrence ? (
                <div>
                  <p className="text-base font-semibold text-foreground leading-snug">
                    {formatOccurrenceDateShortSv(nextOccurrence.start_at)}
                  </p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {formatOccurrenceTimeRangeSv(nextOccurrence.start_at, nextOccurrence.end_at)}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Inga kommande tider just nu.</p>
              )}
            </section>

            <section className="rounded-xl border border-border/60 bg-card p-5">
              <div className="flex items-center gap-2 mb-3">
                <CalendarDays className="h-4 w-4 text-primary" />
                <h2 className="font-medium text-foreground">Kommande tillfällen</h2>
              </div>
              {occurrences && occurrences.length > 0 ? (
                <ul className="divide-y divide-border/40">
                  {occurrences.map((occ) => {
                    const occurrenceOverrideHtml =
                      normalizeOccurrenceDescriptionOverride(occ.description_override) ??
                      normalizeOccurrenceDescriptionOverride(occ.note);
                    const hasOccurrenceImages =
                      (occurrenceImageMap[occ.id]?.length ?? 0) > 0 || resolveOccurrenceImages(occ).length > 0;
                    const isDetailClickable = Boolean(occurrenceOverrideHtml) || hasOccurrenceImages;
                    return (
                    <li key={occ.id} className="py-2.5 first:pt-0 last:pb-0 space-y-1.5">
                      {isDetailClickable ? (
                        <Link
                          to={`/loppis/${detail.flea_market_id}/tillfalle/${occ.id}`}
                          className="group block rounded-md px-1.5 py-1 -mx-1.5 -my-1 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 space-y-1.5">
                              <p className="text-sm font-medium text-foreground">{formatOccurrenceRangeSv(occ.start_at, occ.end_at)}</p>
                              {occurrenceOverrideHtml && (
                                <div className="prose prose-neutral max-w-none prose-p:my-3 prose-ul:my-3 prose-li:my-1 prose-hr:my-6 text-xs text-muted-foreground">
                                  <RichTextRenderer html={occurrenceOverrideHtml} />
                                </div>
                              )}
                            </div>
                            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap pt-0.5">
                              Visa mer
                              <ChevronRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                            </span>
                          </div>
                        </Link>
                      ) : (
                        <>
                          <p className="text-sm font-medium text-foreground">{formatOccurrenceRangeSv(occ.start_at, occ.end_at)}</p>
                          {occurrenceOverrideHtml && (
                            <div className="prose prose-neutral max-w-none prose-p:my-3 prose-ul:my-3 prose-li:my-1 prose-hr:my-6 text-xs text-muted-foreground">
                              <RichTextRenderer html={occurrenceOverrideHtml} />
                            </div>
                          )}
                        </>
                      )}
                    </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">Inga kommande tider just nu.</p>
              )}
            </section>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
